import React from "react";
import { Banner, Button } from "@/ds";
import { ApiError, cockpit, type RequestDetail } from "@/api";
import { useT } from "../../i18n";
import { pathOf } from "../../router";
import type { SendReadinessAction, SendReadinessItem } from "../../../../../core/src/review/send-readiness-types";
import styles from "./send-readiness.module.css";
import { distinctDisagreementEvidence, taskActionLabel } from "./task-presentation";

export type ReadinessLocalIssue = "local_changes" | "refresh_required" | "task_busy";
export const SEND_READINESS_ID = "send-readiness";

/** Server rows and server actions only. Local editing is a separate, explicitly local notice.
 * Two short lists (#2516): the questions the reply asks the customer, and the points for the
 * team, each with at most one step; the details stay folded. */
export function SendReadinessChecklist({ data, localIssue, reload, onBusy, onNavigate }: {
  data: RequestDetail; localIssue?: ReadinessLocalIssue | null; reload: () => Promise<unknown>;
  onBusy: (busy: boolean) => void;
  onNavigate: (action: SendReadinessAction) => void;
}) {
  const t = useT();
  const [busy, setBusy] = React.useState<string | null>(null);
  const busyRef = React.useRef(false);
  const [error, setError] = React.useState<string | null>(null);
  const readiness = data.send_readiness;
  const run = async (key: string, command: () => Promise<unknown>) => {
    if (busyRef.current || localIssue) return;
    busyRef.current = true; setBusy(key); onBusy(true); setError(null);
    try { await command(); await reload(); }
    catch (e) {
      const code = e instanceof ApiError ? (e.data as { code?: string })?.code : null;
      setError(t(`workflow.error.${code}`, undefined, t("workflow.error.generic")));
      await reload();
    } finally { busyRef.current = false; setBusy(null); onBusy(false); }
  };
  const resolve = (taskId: string, action: "approved" | "refused" | "reply_confirmed" | "withdraw_confirmation", field?: string) => {
    if (!data.workflow?.result_id) return;
    const resultId = data.workflow.result_id;
    return run(taskId, async () => {
      if (action === "withdraw_confirmation") {
        const fact = data.facts.find(f => f.fieldPath === field);
        if (!fact || fact.version === undefined) throw new Error("Fact changed");
        await cockpit.confirmFact(data.request.id, fact.fieldPath, false, data.request.current_revision, fact.version);
      } else await cockpit.resolveWorkflowTask(data.request.id, { result_id: resultId,
        task_id: taskId, expected_revision: data.request.current_revision, action, expected_draft_id: data.draft?.id, expected_draft_version: data.draft?.version });
    });
  };
  const addQuestions = () => data.draft && run("questions", () => cockpit.addCustomerQuestions(data.draft!.id, data.draft!.version));

  const taskBlockers = readiness?.blockers.filter(item => item.task_id) ?? [];
  const otherBlockers = readiness?.blockers.filter(item => !item.task_id) ?? [];
  const atSend = readiness?.settled_at_send ?? [];
  const settled = readiness?.information.filter(item => item.task_id) ?? [];
  const questions = readiness?.questions ?? [];
  // Without Sol's own questions, name the details the customer still has to give.
  const customerDetails = questions.length ? [] : readiness?.information.filter(item => item.code === "customer_detail") ?? [];
  const missing = questions.filter(q => !q.in_email).length;

  const buttons = (item: SendReadinessItem) => item.actions.map(action => {
    if (action === "open_profile") return <a key={action} aria-disabled={!!localIssue || !!busy} onClick={event => { if (localIssue || busy) event.preventDefault(); }} href={pathOf({ name: "account" })}>{t("sendReady.action.open_profile")}</a>;
    const resolution = action === "approved" || action === "refused" || action === "reply_confirmed" || action === "withdraw_confirmation";
    return <Button key={action} size="sm" variant={resolution && action !== "withdraw_confirmation" ? "primary" : "secondary"}
      disabled={!!busy || !!localIssue && (resolution || action === "open_assignment" || action === "open_settings")}
      loading={busy === item.task_id && resolution} onClick={() => {
        if (resolution && item.task_id) void resolve(item.task_id, action, item.field);
        else onNavigate(action);
      }}>{t(taskActionLabel(item.task_kind, action, true))}</Button>;
  });
  const details = (item: { detail?: string; disagreement?: SendReadinessItem["disagreement"]; decision?: SendReadinessItem["decision"]; task_kind?: string }) =>
    (item.detail || item.disagreement || item.decision) && <details>
      <summary>{t("sendReady.details")}</summary>
      {item.disagreement && <>
        <p>{t(`factAuthority.check.${item.disagreement.check}`)}</p>
        {distinctDisagreementEvidence(item.disagreement.evidence).map((e, n) => <blockquote key={n}>{e.text}</blockquote>)}
        {data.workflow?.payload?.customer_questions.filter(q => item.disagreement!.question_ids.includes(q.id))
          .map(q => <blockquote key={q.id}>{q.text}</blockquote>)}
      </>}
      {item.detail && <p>{item.detail}</p>}
      {item.decision && <p>{t(item.task_kind === "outside_hamburg"
        ? `sendReady.decision.delivery.${item.decision.action}` : `workflow.resolution.${item.decision.action}`)}
        {item.decision.reason && <>: {item.decision.reason}</>}</p>}
    </details>;

  return <section id={SEND_READINESS_ID} tabIndex={-1} className={styles.checklist} aria-label={t("sendReady.title")} aria-busy={!!busy}>
    <div className={styles.heading}><h3>{t("sendReady.title")}</h3>
      <span role="status">{t(readiness?.ready ? "sendReady.ready" : readiness?.blockers.length === 1 ? "sendReady.openOne" : "sendReady.open", { n: readiness?.blockers.length ?? 1 })}</span>
    </div>
    {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
    {localIssue && <p role="status">{t(`sendReady.local.${localIssue}`)}</p>}
    {!readiness && <p>{t("sendReady.blocker.readiness_unavailable")}</p>}
    {(!readiness || localIssue === "refresh_required") && <Button size="sm" disabled={!!busy}
      onClick={() => onNavigate("refresh")}>{t("sendReady.action.refresh")}</Button>}
    {!!otherBlockers.length && <ul className={styles.list}>
      {otherBlockers.map((item, index) => <li key={`${item.code}:${index}`}>
        <div className={styles.copy}>
          <strong>{t(`sendReady.blocker.${item.code}`)}</strong>
          {item.unavailable_reason && <p>{t(`sendReady.unavailable.${item.unavailable_reason}`)}</p>}
        </div>
        <div className={styles.buttons}>{buttons(item)}</div>
      </li>)}
    </ul>}

    {!!(questions.length || customerDetails.length) && <div className={styles.group}>
      <div className={styles.groupHeading}><h4>{t("sendReady.questions.title")}</h4>
        {readiness?.can_add_questions && <Button size="sm" variant="primary" disabled={!!busy || !!localIssue}
          loading={busy === "questions"} onClick={() => { void addQuestions(); }}>
          {t(missing === 1 ? "sendReady.questions.addOne" : "sendReady.questions.add", { n: missing })}</Button>}
      </div>
      <ul className={styles.compact}>
        {questions.map(q => <li key={q.id}>
          <span className={styles.text}>{q.text}</span>
          <span className={q.in_email ? styles.ok : styles.warn}>{t(q.in_email ? "sendReady.questions.inEmail" : "sendReady.questions.missing")}</span>
        </li>)}
        {customerDetails.map(item => <li key={item.field}><span className={styles.text}>{t(`field.${item.field}`, undefined, item.field)}</span></li>)}
      </ul>
    </div>}

    {!!(taskBlockers.length || atSend.length || settled.length) && <div className={styles.group}>
      <div className={styles.groupHeading}><h4>{t("sendReady.team.title")}</h4></div>
      <ul className={styles.list}>
        {taskBlockers.map(item => <li key={item.task_id}>
          <div className={styles.copy}>
            <strong>{t(`workflow.task.${item.task_kind}`)}</strong>
            {item.field && <p>{t(`field.${item.field}`)}</p>}
            <p className={styles.warn}>{t("sendReady.team.needsClick")}</p>
            {item.decision?.action === "refused" && <p>{t("workflow.afterRefusal")}</p>}
            {item.unavailable_reason && <p>{t(`sendReady.unavailable.${item.unavailable_reason}`)}</p>}
            {details(item)}
          </div>
          <div className={styles.buttons}>{buttons(item)}</div>
        </li>)}
        {atSend.map(item => <li key={item.task_id}>
          <div className={styles.copy}>
            <strong>{t(`workflow.task.${item.task_kind}`)}</strong>
            {item.field && <p>{t(`field.${item.field}`)}</p>}
            <p>{t("sendReady.team.atSend")}</p>
            {details(item)}
          </div>
          {!!item.actions.length && <div className={styles.buttons}>{buttons(item)}</div>}
        </li>)}
        {settled.map(item => <li key={item.task_id}>
          <div className={styles.copy}>
            <strong>{t(`workflow.task.${item.task_kind}`)}</strong>
            {item.disagreement && item.field && <p>{t(`field.${item.field}`)}</p>}
            <p className={item.code === "task_settled" ? styles.ok : undefined}>{t(item.code === "task_settled" ? "sendReady.team.done" : "sendReady.team.information")}</p>
            {details(item)}
          </div>
        </li>)}
      </ul>
    </div>}
  </section>;
}
